// ============================================
// Foundation IAM data layer — role bindings, team membership and the caller's
// own permissions. These endpoints are what the foundation actually decides
// access from (`role_bindings` + `team_members`); the legacy `/projects/:id/teams`
// and `/teams/:id/teammates` routes do not feed it.
//
// `org` in every path below is the DataCentral customer id (e.g. "8064") or the
// organization UUID — the backend accepts either. Catalog lookups need the UUID,
// which `useIamOrg` resolves from `/iam/me`.
// ============================================

export type IamScopeType = 'org' | 'project' | 'resource';
export type IamPrincipalType = 'user' | 'team';

export interface IamRole {
  id: string;
  org_id?: string;
  title: string;
  description: string;
  permissions: string[];
}

/** A permission the foundation knows, as listed by `/iam/catalog`. */
export interface IamPermission {
  name: string;
  service: string;
  resource: string;
  verb: string;
  description: string;
}

/** A role as `/iam/roles` lists it; custom roles belong to one organization. */
export interface IamRoleView extends IamRole {
  custom: boolean;
  slug?: string;
}

export interface IamRoleInput {
  title: string;
  description?: string;
  slug?: string;
  /** Permission patterns; concrete names or wildcards such as `compute.*`. */
  permissions: string[];
}

export interface IamRoleUpdate {
  title?: string;
  description?: string;
  permissions?: string[];
}

export interface IamResourceRef {
  type: string;
  id: string;
  /** Owning service (for compute, its metadata id) — lets the grantee open the resource later. */
  serviceId?: string;
  /** Display name at the time of sharing. A hint: the resource may be renamed afterwards. */
  name?: string;
}

export interface IamBinding {
  id: string;
  org_id: string;
  scope_type: IamScopeType;
  scope_id: string;
  principal_type: IamPrincipalType;
  principal_id: string;
  principal_name: string;
  role_id: string;
  role_title: string;
  /** `directory` bindings are owned by DataCentral and cannot be removed here. */
  source: string;
  created_by?: string;
  created_at: string;
  /** Set on resource-scope bindings, e.g. `compute.instance` and the instance id. */
  resource_type?: string;
  resource_id?: string;
  /** What the sharer said the resource is; unverified unless `context_verified`. */
  resource_service_id?: string;
  resource_name?: string;
  context_verified?: boolean;
}

export interface IamBindingList {
  bindings: IamBinding[];
  inherited?: IamBinding[];
}

export interface IamTeam {
  id: string;
  org_id: string;
  name: string;
  description: string;
  slug: string;
  status: 'active' | 'archived';
  member_count: number;
  created_at: string;
  updated_at: string;
}

export interface IamMember {
  user_id: string;
  email: string;
  display_name: string;
  /** Team-management role, not an access role. */
  role: 'owner' | 'member';
  /** False when the user is not (yet) an active member of the team's organization. */
  org_member: boolean;
  joined_at: string;
}

export interface IamProject {
  id: string;
  org_id: string;
  slug: string;
  name: string;
  description: string;
  is_default: boolean;
  /** The project this one sits under; roles on the parent reach it. Absent or null for a top-level project. */
  parent_id?: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

/** The status the pages show: anything but archived or suspended counts as active. */
export function iamProjectStatus(p: Pick<IamProject, 'status'>): 'active' | 'suspended' | 'archived' {
  return p.status === 'archived' || p.status === 'suspended' ? p.status : 'active';
}

export interface IamOrgRef {
  id: string;
  customer_id: string;
  name: string;
  policy_version: number;
}

export interface IamBindingInput {
  principal_type: IamPrincipalType;
  /** Team id, or a user id already known to the organization. */
  principal_id?: string;
  /** Email of a user; people outside the organization are only allowed at project scope. */
  email?: string;
  role_id: string;
}

export interface IamAddMemberInput {
  user_id?: string;
  email?: string;
}

export interface TeamBindingRow {
  binding: IamBinding;
  scopeLabel: string;
}

/** Permission names the console gates its controls on. */
export const IAM_PERM = {
  bindingsList: 'iam.bindings.list',
  bindingsCreate: 'iam.bindings.create',
  bindingsDelete: 'iam.bindings.delete',
  rolesCreate: 'iam.roles.create',
  rolesUpdate: 'iam.roles.update',
  rolesDelete: 'iam.roles.delete',
  membersCreate: 'teams.members.create',
  membersDelete: 'teams.members.delete',
} as const;

export const iamKeys = {
  all: ['iam'] as const,
  me: ['iam', 'me'] as const,
  catalog: (orgId: string) => ['iam', 'catalog', orgId] as const,
  permissions: (org: string, projectId?: string) =>
    ['iam', 'permissions', org, projectId ?? ''] as const,
  // The project and team lists are the one cache entry behind GET /iam/projects and
  // GET /iam/teams: every screen reads it through useIamProjects / useIamTeams. The
  // request carries no org (the server resolves it from the token), so neither key does.
  /** Prefix of every GET /iam/projects variant (active and archived-included). */
  projectsRoot: ['iam', 'projects'] as const,
  projects: (archived = false) => ['iam', 'projects', archived ? 'all' : 'active'] as const,
  /** GET /iam/teams. */
  teamList: ['iam', 'teams'] as const,
  /** Everything cached under one org's teams: members, invitations, invite link. */
  teams: (org: string) => ['iam', org, 'teams'] as const,
  teamMembers: (org: string, teamId: string) => ['iam', org, 'teams', teamId, 'members'] as const,
  teamInvitations: (org: string, teamId: string) => ['iam', org, 'teams', teamId, 'invitations'] as const,
  inviteLink: (org: string, teamId: string) => ['iam', org, 'teams', teamId, 'invite-link'] as const,
  myInvitations: ['iam', 'me', 'invitations'] as const,
  myJoinedTeams: ['iam', 'me', 'teams'] as const,
  mySharedResources: ['iam', 'me', 'shared-resources'] as const,
  mySharedProjects: ['iam', 'me', 'shared-projects'] as const,
  memberAccess: (userId: string) => ['iam', 'members', userId, 'access'] as const,
  /** GET /workspaces/shared of compute: other organizations reached through a project role or a shared machine. */
  mySharedWorkspaces: ['iam', 'me', 'shared-workspaces'] as const,
  /** GET /iam/members and GET /iam/settings (the org comes from the token). */
  orgMembers: ['iam', 'members'] as const,
  orgSettings: ['iam', 'settings'] as const,
  permissionCatalog: ['iam', 'catalog', 'permissions'] as const,
  orgRoles: (org: string) => ['iam', org, 'roles'] as const,
  resourceBindings: (org: string, type = '', id = '') =>
    ['iam', org, 'bindings', 'resource', type, id] as const,
  orgBindings: (org: string) => ['iam', org, 'bindings', 'org'] as const,
  projectBindings: (org: string, projectId: string) =>
    ['iam', org, 'bindings', 'project', projectId] as const,
  bindingsRoot: (org: string) => ['iam', org, 'bindings'] as const,
};
